import Link from "next/link"
import { MapPin, Clock, Tag, DollarSign, Users } from "lucide-react"

const URGENCY_STYLES = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  normal: "bg-blue-50 text-blue-700 border-blue-200",
  flexible: "bg-gray-100 text-gray-600 border-gray-200",
}
const URGENCY_LABELS = { urgent: "Urgent", normal: "Normal", flexible: "Flexible" }

const STATUS_STYLES: Record<string, string> = {
  open: "bg-emerald-50 text-emerald-700",
  awaiting_scope: "bg-purple-50 text-purple-700",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-50 text-red-600",
}
const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  awaiting_scope: "Awaiting Scope",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
}

interface ProjectCardProps {
  project: {
    id: string
    title: string
    description: string
    location: string
    category: string
    budget?: string | null
    timeline?: string | null
    urgency: "flexible" | "normal" | "urgent"
    tags: string[]
    status: "open" | "awaiting_scope" | "in_progress" | "completed" | "cancelled"
    createdAt: string
    postedBy: { id: string; name: string; userType: string; profileImage?: string | null }
    _count: { bids: number }
  }
  showStatus?: boolean
}

export default function ProjectCard({ project, showStatus = false }: ProjectCardProps) {
  const timeAgo = (() => {
    const diff = Date.now() - new Date(project.createdAt).getTime()
    const days = Math.floor(diff / 86400000)
    if (days === 0) return "Today"
    if (days === 1) return "Yesterday"
    if (days < 7) return `${days}d ago`
    return new Date(project.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
  })()

  return (
    <Link href={`/projects/${project.id}`} className="block group">
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-1">
              {project.title}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 capitalize">{project.category}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${URGENCY_STYLES[project.urgency]}`}>
              {URGENCY_LABELS[project.urgency]}
            </span>
            {showStatus && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[project.status]}`}>
                {STATUS_LABELS[project.status]}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{project.description}</p>

        {/* Tags */}
        {project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {project.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
            {project.tags.length > 4 && (
              <span className="text-xs text-gray-400">+{project.tags.length - 4} more</span>
            )}
          </div>
        )}

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {project.location}
          </span>
          {project.budget && (
            <span className="flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" />
              {project.budget}
            </span>
          )}
          {project.timeline && (
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {project.timeline}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {project._count.bids} bid{project._count.bids !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 flex items-center justify-center text-gray-500 text-xs font-bold">
              {project.postedBy.profileImage ? (
                <img src={project.postedBy.profileImage} alt="" className="w-full h-full object-cover" />
              ) : (
                project.postedBy.name.charAt(0).toUpperCase()
              )}
            </div>
            <span className="text-xs text-gray-600">{project.postedBy.name}</span>
          </div>
          <span className="text-xs text-gray-400">{timeAgo}</span>
        </div>
      </div>
    </Link>
  )
}
